import { navigate } from '@/utils/navigate';
import { orgService } from '@/services/org-service';
import './nu-user-menu';
import { scanService, type ScanSubmission } from '@/services/scan-service';
import { ticketService, type TicketAction, type TicketActionResult } from '@/services/ticket-service';
import { applyDevWatermark } from '@/services/dev-watermark';

type ScanMode = 'checkin' | 'checkout';

class NuScan extends HTMLElement {
  protected readonly root: ShadowRoot;
  private mode: ScanMode = 'checkin';
  private photoUrl: string | null = null;
  private _photoBlob: Blob | null = null;
  private ticketNum = '';
  private phoneNum = '';
  private subscriptionToken = '';
  private optInAccepted = false;
  private _canCheckIn = true;
  private _canCheckOut = true;
  private _resolvedOrgName = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() { return ['org-id', 'access-id']; }

  get orgId() { return this.getAttribute('org-id') ?? ''; }
  get accessId() { return this.getAttribute('access-id') ?? ''; }

  connectedCallback() {
    applyDevWatermark(this.root);
    if (this.orgId) {
      this._renderTokenLoading();
      this._loadOrgScan();
    } else if (this.accessId) {
      this._renderTokenLoading();
      this._loadToken();
    }
  }

  private _bindPhotoCapture() {
    this.root.addEventListener('photo-captured', (e: Event) => {
      const blob = (e as CustomEvent<Blob>).detail;
      if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
      this.photoUrl = URL.createObjectURL(blob);
      this._photoBlob = blob;
      this._showLoading();
    });
  }

  private async _loadOrgScan() {
    try {
      const [org, ents] = await Promise.all([
        orgService.getOrgById(this.orgId),
        orgService.getOrgEntitlements(this.orgId),
      ]);
      this._resolvedOrgName = org.name;
      this._canCheckIn = ents.includes('ac/org/scan/check-in');
      this._canCheckOut = ents.includes('ac/org/scan/check-out');
      if (!this._canCheckIn && !this._canCheckOut) {
        this._renderTokenError('You do not have access to this organization.');
        return;
      }
      if (!this._canCheckIn && this._canCheckOut) {
        this.mode = 'checkout';
      }
      this._bindPhotoCapture();
      this._initialRender();
    } catch (err) {
      const msg = err instanceof Error && err.message === 'forbidden'
        ? 'You do not have access to this organization.'
        : err instanceof Error && err.message === 'not_found'
          ? 'Organization not found.'
          : 'Failed to load organization.';
      this._renderTokenError(msg);
    }
  }

  private async _loadToken() {
    try {
      const info = await orgService.getAnonToken(this.accessId);
      this._resolvedOrgName = info.orgName;
      this._canCheckIn = info.roles.includes('CHECK-IN');
      this._canCheckOut = info.roles.includes('CHECK-OUT');
      if (!this._canCheckIn && this._canCheckOut) {
        this.mode = 'checkout';
      }
      this._bindPhotoCapture();
      this._initialRender();
    } catch (err) {
      const msg = err instanceof Error && err.message === 'disabled'
        ? 'This access token has been disabled.'
        : err instanceof Error && err.message === 'not_found'
          ? 'Access token not found.'
          : 'Failed to load token info.';
      this._renderTokenError(msg);
    }
  }

  private _renderTokenLoading() {
    this.root.innerHTML = this._styles() + `
      <div class="page">
        <header class="top-bar">
          <span class="brand"><span class="dot"></span>notify-u</span>
        </header>
        <div class="token-status">
          <div class="spinner-lg"></div>
        </div>
      </div>
    `;
  }

  private _renderTokenError(msg: string) {
    this.root.innerHTML = this._styles() + `
      <div class="page">
        <header class="top-bar">
          <span class="brand"><span class="dot"></span>notify-u</span>
        </header>
        <div class="token-status">
          <p class="token-error mono">${msg}</p>
        </div>
      </div>
    `;
  }

  disconnectedCallback() {
    if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
    this._photoBlob = null;
  }

  // ---- one-time render ----

  private _initialRender() {
    this.root.innerHTML = this._styles() + `
      <div class="page">
        <header class="top-bar">
          <span class="brand"><span class="dot"></span>notify-u</span>
          <div class="header-right">
            <span class="scan-id mono">${this._resolvedOrgName}</span>
            ${this.orgId ? `
            <button class="btn-back" id="back-btn">← Back</button>
            ` : ''}
            <nu-user-menu></nu-user-menu>
          </div>
        </header>

        <div class="mode-toggle">
          <button class="toggle-btn ${this.mode === 'checkin' ? 'active' : ''} ${!this._canCheckIn ? 'disabled' : ''}" data-mode="checkin">Check In</button>
          <button class="toggle-btn ${this.mode === 'checkout' ? 'active' : ''} ${!this._canCheckOut ? 'disabled' : ''}" data-mode="checkout">Check Out</button>
        </div>

        <div class="camera-section content">
          <p class="hint mono">Scan the drop-off ticket for check in</p>
          <nu-camera-capture></nu-camera-capture>
        </div>

        <div class="confirm-section content" style="display:none"></div>
      </div>
      <div class="toast" aria-live="polite"></div>
      <div class="cam-backdrop" id="cam-backdrop"></div>
      <div class="cam-drawer" id="cam-drawer">
        <div class="cam-drawer-handle"></div>
        <div class="cam-drawer-label">Camera</div>
        <button class="cam-drawer-item" id="cam-switch">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M1 4v6h6"/>
            <path d="M23 20v-6h-6"/>
            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"/>
          </svg>
          Switch Camera
        </button>
      </div>
    `;

    this.root.querySelector('#back-btn')?.addEventListener('click', () => navigate('/app'));

    // camera settings drawer
    this.root.addEventListener('camera-settings', () => this._openCameraDrawer());
    this.root.querySelector('#cam-backdrop')!.addEventListener('click', () => this._closeCameraDrawer());
    this.root.querySelector('#cam-switch')!.addEventListener('click', () => {
      this._closeCameraDrawer();
      const cam = this.root.querySelector('nu-camera-capture') as HTMLElement & { switchCamera(): void };
      cam?.switchCamera();
    });

    this.root.querySelectorAll<HTMLElement>('.toggle-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const newMode = btn.dataset.mode as ScanMode;
        if (newMode === this.mode) return;
        if (newMode === 'checkin' && !this._canCheckIn) return;
        if (newMode === 'checkout' && !this._canCheckOut) return;
        this.mode = newMode;
        this._updateToggle();
        // if not already on camera, discard and return
        if (this.root.querySelector<HTMLElement>('.camera-section')!.style.display === 'none') {
          if (this.photoUrl) { URL.revokeObjectURL(this.photoUrl); this.photoUrl = null; }
          this._showCamera();  // also cancels any in-flight simulated load
        }
      });
    });
  }

  // ---- section show/hide ----

  private _showCamera() {
    this.root.querySelector<HTMLElement>('.camera-section')!.style.display = '';
    this.root.querySelector<HTMLElement>('.confirm-section')!.style.display = 'none';
  }

  private _showLoading() {
    const section = this.root.querySelector<HTMLElement>('.confirm-section')!;
    section.innerHTML = `
      <div class="ticket-card">
        <div class="perf"></div>
        <div class="card-body">
          <div class="photo-wrap">
            <img src="${this.photoUrl}" alt="Captured ticket">
            <div class="photo-overlay"><div class="spinner"></div></div>
          </div>
        </div>
        <div class="perf"></div>
      </div>
    `;
    section.style.display = '';
    this.root.querySelector<HTMLElement>('.camera-section')!.style.display = 'none';
    this._submitScan();
  }

  private async _submitScan() {
    if (!this._photoBlob) return;
    const blob = this._photoBlob;
    try {
      let result: ScanSubmission;
      if (this.orgId) {
        result = await scanService.submitOrgScan(this.orgId, blob);
      } else {
        result = await scanService.submitAnonScan(this.accessId, blob);
      }
      this._photoBlob = null;
      this.ticketNum         = result.extractedId ?? '';
      this.phoneNum          = result.extractedPhone ?? '';
      this.subscriptionToken = result.extractedToken ?? '';
      this._showConfirm();
    } catch (_err) {
      this._showScanError();
    }
  }

  private _showScanError() {
    const section = this.root.querySelector<HTMLElement>('.confirm-section')!;
    section.innerHTML = `
      <div class="ticket-card">
        <div class="perf"></div>
        <div class="card-body center">
          <p class="token-error mono">Failed to submit ticket. Please try again.</p>
        </div>
        <div class="perf"></div>
      </div>
      <button class="btn-ghost" id="retake-btn">Retake photo</button>
    `;
    section.querySelector('#retake-btn')!.addEventListener('click', () => this._showCamera());
  }

  private _showConfirm() {
    const isCheckIn = this.mode === 'checkin';
    const section = this.root.querySelector<HTMLElement>('.confirm-section')!;
    section.innerHTML = `
      <div class="ticket-card">
        <div class="perf"></div>
        <div class="card-body">
          <div class="photo-wrap">
            <img src="${this.photoUrl}" alt="Captured ticket">
          </div>
          <label class="eyebrow mono" for="ticket-num">
            Ticket #${!this.ticketNum ? ' <span class="not-found-badge">not detected</span>' : ''}
          </label>
          <input class="field-input${!this.ticketNum ? ' field-empty' : ''}" id="ticket-num" type="text"
            value="${this.ticketNum}" placeholder="e.g. 0482" inputmode="numeric" autocomplete="off">
          ${isCheckIn ? `
          <label class="eyebrow mono" for="phone-num" style="margin-top:14px;display:block;">
            Phone${!this.phoneNum ? ' <span class="not-found-badge">not detected</span>' : ''}
          </label>
          <input class="field-input${!this.phoneNum ? ' field-empty' : ''}" id="phone-num" type="tel"
            value="${this.phoneNum}" placeholder="Enter phone number" autocomplete="off">
          ${this.subscriptionToken ? `
          <div class="qr-linked-badge">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="5" height="5" rx="1" fill="currentColor"/>
              <rect x="8" y="0" width="5" height="5" rx="1" fill="currentColor"/>
              <rect x="0" y="8" width="5" height="5" rx="1" fill="currentColor"/>
              <rect x="8" y="8" width="2" height="2" fill="currentColor"/>
              <rect x="11" y="8" width="2" height="2" fill="currentColor"/>
              <rect x="8" y="11" width="2" height="2" fill="currentColor"/>
              <rect x="11" y="11" width="2" height="2" fill="currentColor"/>
            </svg>
            QR sticker linked — customers can scan to subscribe
          </div>
          ` : `
          <label class="opt-in-row" for="opt-in-check">
            <input type="checkbox" id="opt-in-check" class="opt-in-checkbox">
            <span class="opt-in-label">Customer verbally accepted opt-in</span>
          </label>
          `}
          ` : ''}
        </div>
        <div class="perf"></div>
      </div>
      <button class="btn-primary" id="confirm-btn">
        ${isCheckIn ? 'Confirm &amp; check in' : 'Confirm &amp; mark ready'}
      </button>
      <button class="btn-ghost" id="retake-btn">Retake photo</button>
    `;

    const confirmBtn = section.querySelector<HTMLButtonElement>('#confirm-btn')!;
    const phoneInput  = section.querySelector<HTMLInputElement>('#phone-num');
    const optInCheck  = section.querySelector<HTMLInputElement>('#opt-in-check');

    const updateConfirmState = () => {
      const hasPhone = !!(phoneInput?.value.trim());
      const optedIn  = !!(optInCheck?.checked);
      const blocked  = hasPhone && !optedIn;
      confirmBtn.classList.toggle('needs-optin', blocked);
    };

    phoneInput?.addEventListener('input', updateConfirmState);
    optInCheck?.addEventListener('change', updateConfirmState);
    updateConfirmState();

    confirmBtn.addEventListener('click', async () => {
      this.ticketNum = (section.querySelector<HTMLInputElement>('#ticket-num')?.value ?? '').trim();
      this.phoneNum  = (section.querySelector<HTMLInputElement>('#phone-num')?.value  ?? '').trim();
      this.optInAccepted = (section.querySelector<HTMLInputElement>('#opt-in-check')?.checked ?? false);

      if (!this.ticketNum) {
        this._toast('Enter a ticket number');
        return;
      }

      if (this.phoneNum && !this.optInAccepted && !this.subscriptionToken) {
        this._toast('Check the opt-in box before proceeding', 'warn');
        return;
      }

      const action: TicketAction = this.mode === 'checkin' ? 'CHECK_IN' : 'CHECK_OUT';
      const optInAcceptedTS = (action === 'CHECK_IN' && !this.subscriptionToken && this.optInAccepted)
        ? new Date().toISOString()
        : null;
      confirmBtn.disabled = true;

      try {
        let result: TicketActionResult;
        if (this.orgId) {
          result = await ticketService.submitOrgTicketAction(this.orgId, this.ticketNum, action, this.phoneNum || undefined, this.subscriptionToken || undefined, optInAcceptedTS);
        } else {
          result = await ticketService.submitAnonTicketAction(this.accessId, this.ticketNum, action, this.phoneNum || undefined, this.subscriptionToken || undefined, optInAcceptedTS);
        }
        const label = `#${this.ticketNum}`;
        this.ticketNum         = '';
        this.phoneNum          = '';
        this.subscriptionToken = '';
        this.optInAccepted     = false;
        this._showCamera();
        this._notifyToast(label, action, result);
      } catch (err) {
        confirmBtn.disabled = false;
        if (err instanceof Error && err.message === 'already_checked_in') {
          this._toast(`Ticket already checked in`);
        } else if (err instanceof Error && err.message === 'not_found') {
          this._toast(`No active ticket found for that number`);
        } else {
          this._toast('Failed to submit — please try again');
        }
      }
    });

    section.querySelector('#retake-btn')!.addEventListener('click', () => {
      this._showCamera();
    });

    section.style.display = '';
    this.root.querySelector<HTMLElement>('.camera-section')!.style.display = 'none';
  }

  private _toast(msg: string, variant: 'default' | 'warn' | 'error' = 'default') {
    const el = this.root.querySelector<HTMLElement>('.toast')!;
    el.textContent = msg;
    el.className = 'toast';
    if (variant !== 'default') el.classList.add(`toast-${variant}`);
    el.classList.add('toast-in');
    setTimeout(() => {
      el.classList.replace('toast-in', 'toast-out');
    }, 3200);
  }

  private _notifyToast(label: string, action: TicketAction, result: TicketActionResult) {
    const verb = action === 'CHECK_IN' ? 'checked in' : 'ready';
    const ns = result.notifyStatus;
    if (!ns || ns === 'NO_PHONE') {
      this._toast(`${label} ${verb} · no phone on file`);
    } else if (ns === 'SENT') {
      this._toast(`${label} ${verb} · text sent`);
    } else if (ns === 'NUM-OPT-OUT') {
      this._toast(`${label} ${verb} · customer not opted in`, 'warn');
    } else {
      this._toast(`${label} ${verb} · text failed to send`, 'error');
    }
  }

  // ---- camera drawer ----

  private _openCameraDrawer() {
    const backdrop = this.root.querySelector<HTMLElement>('#cam-backdrop')!;
    const drawer   = this.root.querySelector<HTMLElement>('#cam-drawer')!;
    requestAnimationFrame(() => {
      backdrop.classList.add('open');
      drawer.classList.add('open');
    });
  }

  private _closeCameraDrawer() {
    this.root.querySelector<HTMLElement>('#cam-backdrop')!.classList.remove('open');
    this.root.querySelector<HTMLElement>('#cam-drawer')!.classList.remove('open');
  }

  // ---- toggle ----

  private _updateToggle() {
    this.root.querySelectorAll<HTMLElement>('.toggle-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.mode === this.mode);
      const can = btn.dataset.mode === 'checkin' ? this._canCheckIn : this._canCheckOut;
      btn.classList.toggle('disabled', !can);
    });
    const hint = this.root.querySelector<HTMLElement>('.hint');
    if (hint) {
      hint.textContent = this.mode === 'checkin'
        ? 'Scan the ticket for check in'
        : 'Scan the ticket to send notifications';
    }
  }

  // ---- styles ----

  private _styles(): string {
    return `<style>
      :host { display: block; }
      *, *::before, *::after { box-sizing: border-box; }

      .page {
        min-height: 100dvh;
        background: var(--paper, #E4DED2);
        color: var(--ink, #2A2622);
        font-family: 'Inter', sans-serif;
        line-height: 1.55;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .mono { font-family: 'IBM Plex Mono', monospace; }
      a { color: inherit; text-decoration: none; }

      /* ---- header ---- */
      .top-bar {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 20px;
        border-bottom: 1px solid var(--line, #C9C0AC);
        background: rgba(228,222,210,0.92);
        backdrop-filter: blur(6px);
        position: sticky;
        top: 0;
        z-index: 10;
      }
      .brand {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700;
        font-size: 18px;
        letter-spacing: -0.02em;
        display: flex;
        align-items: center;
        gap: 7px;
      }
      .dot {
        width: 8px; height: 8px;
        border-radius: 50%;
        background: var(--kiln, #C97A2E);
        display: inline-block;
        flex-shrink: 0;
      }
      .header-right {
        display: flex;
        align-items: center;
        gap: 8px;
        position: relative;
      }
      .scan-id {
        font-size: 12px;
        color: var(--ink-soft, #5A5346);
      }
      .btn-back {
        background: transparent;
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px; padding: 5px 12px;
        font-family: 'Inter', sans-serif; font-size: 13px;
        color: var(--ink-soft, #5A5346); cursor: pointer; transition: background .15s;
      }
      .btn-back:hover { background: var(--paper-light, #FAF7F0); }

      /* ---- mode toggle ---- */
      .mode-toggle {
        display: flex;
        margin: 20px auto 0;
        width: calc(100% - 40px);
        max-width: 380px;
        background: var(--paper-light, #FAF7F0);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
        overflow: hidden;
      }
      .toggle-btn {
        flex: 1;
        padding: 11px 0;
        border: none;
        background: transparent;
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 14px;
        color: var(--ink-soft, #5A5346);
        cursor: pointer;
        transition: background .15s, color .15s;
      }
      .toggle-btn.active {
        background: var(--kiln, #C97A2E);
        color: var(--paper-light, #FAF7F0);
      }
      .toggle-btn.disabled {
        opacity: 0.38;
        cursor: not-allowed;
      }
      .toggle-btn:not(.active):not(.disabled):hover {
        background: var(--paper, #E4DED2);
        color: var(--ink, #2A2622);
      }

      /* ---- content area ---- */
      .content {
        width: 100%;
        max-width: 420px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .hint {
        margin: 0;
        font-size: 12px;
        color: var(--ink-soft, #5A5346);
        letter-spacing: .04em;
        text-align: center;
      }

      /* ---- ticket card ---- */
      .ticket-card {
        background: var(--paper-light, #FAF7F0);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
        overflow: hidden;
      }
      .perf {
        height: 12px;
        background-image: radial-gradient(circle at 6px 6px, var(--paper, #E4DED2) 4px, var(--paper-light, #FAF7F0) 4.3px);
        background-size: 16px 12px;
        background-repeat: repeat-x;
      }
      .card-body {
        padding: 20px 22px 24px;
        display: flex;
        flex-direction: column;
      }
      .card-body.center {
        align-items: center;
        text-align: center;
        gap: 10px;
        padding: 28px 22px;
      }
      .eyebrow {
        font-size: 11px;
        letter-spacing: .08em;
        text-transform: uppercase;
        color: var(--kiln-dark, #A15F1F);
        margin: 0 0 6px;
        display: block;
      }

      /* ---- photo preview ---- */
      .photo-wrap {
        position: relative;
        margin-bottom: 18px;
        border-radius: 5px;
        overflow: hidden;
        border: 1px dashed var(--line, #C9C0AC);
        background: var(--paper, #E4DED2);
      }
      .photo-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(42, 38, 34, 0.5);
      }
      .photo-wrap img {
        width: 100%;
        display: block;
        max-height: 180px;
        object-fit: cover;
      }

      /* ---- not-detected badge ---- */
      .not-found-badge {
        display: inline-block;
        font-size: 10px;
        letter-spacing: .04em;
        text-transform: uppercase;
        background: var(--paper, #E4DED2);
        border: 1px solid var(--line, #C9C0AC);
        color: var(--ink-soft, #5A5346);
        border-radius: 3px;
        padding: 1px 5px;
        vertical-align: middle;
        margin-left: 4px;
      }
      .field-empty {
        border-style: dashed;
        color: var(--ink-soft, #5A5346);
      }
      .qr-linked-badge {
        display: flex;
        align-items: center;
        gap: 7px;
        margin-top: 10px;
        padding: 8px 10px;
        background: #EFF4EA;
        border: 1px solid #B5CC9E;
        border-radius: 5px;
        font-family: 'IBM Plex Mono', monospace;
        font-size: 11px;
        color: #4A6035;
        letter-spacing: .02em;
      }

      /* ---- opt-in checkbox ---- */
      .opt-in-row {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-top: 14px;
        cursor: pointer;
      }
      .opt-in-checkbox {
        width: 16px; height: 16px;
        accent-color: var(--kiln, #C97A2E);
        cursor: pointer;
        flex-shrink: 0;
      }
      .opt-in-label {
        font-size: 13px;
        color: var(--ink-soft, #5A5346);
        line-height: 1.4;
      }

      /* ---- inputs ---- */
      .field-input {
        display: block;
        width: 100%;
        background: var(--paper, #E4DED2);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px;
        padding: 10px 12px;
        font-family: 'IBM Plex Mono', monospace;
        font-size: 15px;
        color: var(--ink, #2A2622);
      }
      .field-input:focus {
        outline: 2px solid var(--kiln, #C97A2E);
        outline-offset: 1px;
        border-color: transparent;
      }

      /* ---- buttons ---- */
      .btn-primary {
        display: block;
        width: 100%;
        background: var(--kiln, #C97A2E);
        color: var(--paper-light, #FAF7F0);
        border: none;
        border-radius: 5px;
        padding: 14px;
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 15px;
        cursor: pointer;
        transition: background .15s, transform .1s;
      }
      .btn-primary:hover { background: var(--kiln-dark, #A15F1F); }
      .btn-primary:active { transform: translateY(1px); }
      .btn-primary.needs-optin {
        background: repeating-linear-gradient(
          -45deg,
          #b5860d 0px,
          #b5860d 6px,
          #d4a017 6px,
          #d4a017 12px
        );
        color: #1a1200;
        cursor: pointer;
      }
      .btn-primary.needs-optin:hover { filter: brightness(1.08); }
      .btn-ghost {
        display: block;
        width: 100%;
        background: transparent;
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px;
        padding: 12px;
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        color: var(--ink-soft, #5A5346);
        cursor: pointer;
        transition: background .15s;
      }
      .btn-ghost:hover { background: var(--paper-light, #FAF7F0); }

      /* ---- success ---- */
      .check-circle {
        width: 60px; height: 60px;
        border-radius: 50%;
        background: var(--glaze, #6E7F5C);
        color: #fff;
        font-size: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .success-num {
        font-size: 20px;
        font-weight: 500;
        color: var(--ink, #2A2622);
      }
      .success-msg {
        font-size: 14px;
        color: var(--ink-soft, #5A5346);
        max-width: 30ch;
        margin: 0;
      }
      .status-row {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: var(--ink-soft, #5A5346);
      }
      .dot-pulse {
        width: 8px; height: 8px;
        border-radius: 50%;
        background: var(--glaze, #6E7F5C);
        flex-shrink: 0;
        animation: pulse 1.8s ease-in-out infinite;
      }
      /* ---- token loading / error ---- */
      .token-status {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 48px 20px;
      }
      .spinner-lg {
        width: 36px; height: 36px;
        border: 3px solid var(--line, #C9C0AC);
        border-top-color: var(--kiln, #C97A2E);
        border-radius: 50%;
        animation: spin .7s linear infinite;
      }
      .token-error {
        font-size: 13px;
        color: #b94a2c;
        text-align: center;
        margin: 0;
      }

      /* ---- spinner ---- */
      .spinner {
        width: 36px; height: 36px;
        border: 3px solid rgba(250, 247, 240, 0.35);
        border-top-color: var(--paper-light, #FAF7F0);
        border-radius: 50%;
        animation: spin .8s linear infinite;
      }
      @keyframes spin { to { transform: rotate(360deg); } }

      @keyframes pulse { 0%,100%{ opacity:1; } 50%{ opacity:.35; } }
      @media (prefers-reduced-motion: reduce) { .dot-pulse { animation: none; } }

      /* ---- toast ---- */
      .toast {
        position: fixed;
        bottom: 24px;
        left: 50%;
        transform: translateX(-50%);
        background: var(--ink, #2A2622);
        color: var(--paper-light, #FAF7F0);
        font-family: 'IBM Plex Mono', monospace;
        font-size: 13px;
        padding: 12px 20px;
        border-radius: 6px;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
      }
      .toast-in  { animation: toast-in  .2s ease forwards; }
      .toast-out { animation: toast-out .4s ease forwards; }
      .toast-warn  { background: #7A4E1A; }
      .toast-error { background: #7A2A1C; }
      @keyframes toast-in  { to   { opacity: 1; transform: translateX(-50%) translateY(0);    } }
      @keyframes toast-out { from { opacity: 1; } to { opacity: 0; transform: translateX(-50%) translateY(6px); } }

      /* ---- camera settings drawer ---- */
      .cam-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(42, 38, 34, 0.45);
        z-index: 400;
        opacity: 0;
        pointer-events: none;
        transition: opacity .25s;
      }
      .cam-backdrop.open { opacity: 1; pointer-events: auto; }

      .cam-drawer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: var(--paper-light, #FAF7F0);
        border-radius: 14px 14px 0 0;
        padding: 10px 0 calc(env(safe-area-inset-bottom, 0px) + 20px);
        z-index: 401;
        transform: translateY(100%);
        transition: transform .3s cubic-bezier(.32, .72, 0, 1);
        box-shadow: 0 -4px 24px rgba(42, 38, 34, .18);
      }
      .cam-drawer.open { transform: translateY(0); }

      .cam-drawer-handle {
        width: 36px; height: 4px;
        background: var(--line, #C9C0AC);
        border-radius: 2px;
        margin: 0 auto 14px;
      }
      .cam-drawer-label {
        font-family: 'IBM Plex Mono', monospace;
        font-size: 11px;
        font-weight: 500;
        letter-spacing: .1em;
        text-transform: uppercase;
        color: var(--ink-soft, #5A5346);
        padding: 0 24px 8px;
      }
      .cam-drawer-item {
        display: flex;
        align-items: center;
        gap: 14px;
        width: 100%;
        padding: 14px 24px;
        background: transparent;
        border: none;
        font-family: 'Inter', sans-serif;
        font-size: 15px;
        font-weight: 500;
        color: var(--ink, #2A2622);
        cursor: pointer;
        text-align: left;
        transition: background .12s;
        -webkit-tap-highlight-color: transparent;
      }
      .cam-drawer-item:hover  { background: var(--paper, #E4DED2); }
      .cam-drawer-item:active { background: var(--paper, #E4DED2); }
      .cam-drawer-item svg { flex-shrink: 0; color: var(--ink-soft, #5A5346); }
    </style>`;
  }
}

customElements.define('nu-scan', NuScan);